/*
 * Bonvelle · Rhode-laag: inschuiven en schuifrijen.
 *
 * 1. [data-rh-in]: secties schuiven 5% omhoog in zodra ze in beeld komen (Rhode: GSAP, yPercent 5,
 *    1s); beelden met .rh-zoom zoomen van 1.12 naar 1. Stil onder prefers-reduced-motion: dan
 *    krijgt niets de beginstand, dus niets kan onzichtbaar blijven.
 * 2. [data-rh-slider]: een rij met scroll-snap. Pijlen schuiven één kaart, de balk toont de stand,
 *    met de muis kun je slepen, en op desktop volgt een glazen "swipe"-cirkel de muis (Rhode).
 *
 * Horizon scrolt op desktop in .page-wrapper; IntersectionObserver met de viewport als root werkt
 * daar gewoon mee.
 */
(() => {
  const rustig = matchMedia('(prefers-reduced-motion: reduce)');
  const fijn = matchMedia('(hover: hover) and (pointer: fine)');
  if (!rustig.matches) document.documentElement.classList.add('rh-beweeg');

  const io =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (items) =>
            items.forEach((i) => {
              if (!i.isIntersecting) return;
              i.target.classList.add('is-in');
              io.unobserve(i.target);
            }),
          { rootMargin: '0px 0px -6% 0px', threshold: 0.04 }
        )
      : null;

  const inschuiven = (root) =>
    root.querySelectorAll('[data-rh-in]:not(.is-in)').forEach((el) => (io ? io.observe(el) : el.classList.add('is-in')));

  const cursor = (() => {
    const c = document.createElement('div');
    c.className = 'rh-swipe';
    c.setAttribute('aria-hidden', 'true');
    c.textContent = 'swipe';
    return c;
  })();

  const slider = (el) => {
    if (el.dataset.rhKlaar) return;
    el.dataset.rhKlaar = '1';
    const spoor = el.querySelector('[data-rh-spoor]');
    if (!spoor) return;
    const vul = el.querySelector('[data-rh-balk]');
    const vorige = el.querySelector('[data-rh-vorige]');
    const volgende = el.querySelector('[data-rh-volgende]');

    const stap = () => {
      const kaart = spoor.firstElementChild;
      if (!kaart) return spoor.clientWidth;
      const gap = parseFloat(getComputedStyle(spoor).columnGap) || 0;
      return kaart.getBoundingClientRect().width + gap;
    };
    const stand = () => {
      const max = spoor.scrollWidth - spoor.clientWidth;
      const deel = spoor.clientWidth / Math.max(spoor.scrollWidth, 1);
      if (vul) {
        vul.style.width = `${Math.max(deel, 0.08) * 100}%`;
        vul.style.transform = `translateX(${max > 0 ? (spoor.scrollLeft / max) * (1 / Math.max(deel, 0.08) - 1) * 100 : 0}%)`;
      }
      if (vorige) vorige.disabled = spoor.scrollLeft <= 2;
      if (volgende) volgende.disabled = spoor.scrollLeft >= max - 2;
      el.classList.toggle('rh-rij--vol', max <= 2);
    };
    vorige?.addEventListener('click', () => spoor.scrollBy({ left: -stap(), behavior: rustig.matches ? 'auto' : 'smooth' }));
    volgende?.addEventListener('click', () => spoor.scrollBy({ left: stap(), behavior: rustig.matches ? 'auto' : 'smooth' }));
    spoor.addEventListener('scroll', stand, { passive: true });
    new ResizeObserver(stand).observe(spoor);
    stand();

    // Slepen met de muis; vinger en trackpad scrollen al vanzelf.
    let sleep = null;
    spoor.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      sleep = { x: e.clientX, links: spoor.scrollLeft, bewogen: false };
    });
    window.addEventListener('pointermove', (e) => {
      if (!sleep) return;
      const dx = e.clientX - sleep.x;
      if (!sleep.bewogen && Math.abs(dx) > 5) {
        sleep.bewogen = true;
        spoor.classList.add('is-slepen');
      }
      if (sleep.bewogen) spoor.scrollLeft = sleep.links - dx;
    });
    window.addEventListener('pointerup', () => {
      if (!sleep) return;
      const was = sleep.bewogen;
      sleep = null;
      spoor.classList.remove('is-slepen');
      if (was) {
        // Laat de klik die op het slepen volgt geen link openen.
        spoor.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); }, { capture: true, once: true });
        spoor.scrollBy({ left: 0 });
      }
    });

    // Glazen "swipe"-cirkel (alleen muis, alleen met beweging).
    if (fijn.matches && !rustig.matches) {
      spoor.addEventListener('pointerenter', () => document.body.appendChild(cursor));
      spoor.addEventListener('pointerleave', () => cursor.classList.remove('is-zichtbaar'));
      spoor.addEventListener('pointermove', (e) => {
        cursor.classList.add('is-zichtbaar');
        cursor.style.transform = `translate(${e.clientX - 60}px, ${e.clientY - 60}px)`;
      });
    }
  };

  // SHOP-menu: tabbladen wisselen bij klik, hover en pijltjes (Rhode: tabs met schuivende lijn).
  const mega = (el) => {
    if (el.dataset.rhKlaar) return;
    el.dataset.rhKlaar = '1';
    const tabs = [...el.querySelectorAll('[data-rh-mega-tab]')];
    const kies = (tab, focus) => {
      tabs.forEach((t) => {
        const aan = t === tab;
        t.setAttribute('aria-selected', String(aan));
        t.tabIndex = aan ? 0 : -1;
        const p = document.getElementById(t.getAttribute('aria-controls'));
        if (p) p.hidden = !aan;
      });
      if (focus) tab.focus();
    };
    tabs.forEach((t, i) => {
      t.addEventListener('click', () => kies(t));
      if (fijn.matches) t.addEventListener('pointerenter', () => kies(t));
      t.addEventListener('keydown', (e) => {
        const stap = { ArrowRight: 1, ArrowLeft: -1 }[e.key];
        if (!stap) return;
        e.preventDefault();
        kies(tabs[(i + stap + tabs.length) % tabs.length], true);
      });
    });
  };

  // Aanbevolen producten: de sectie haalt zichzelf op via Shopify's aanbevelingen.
  const aanbevolen = async (el) => {
    if (el.dataset.rhAanbevolen === 'bezig' || el.querySelector('[data-rh-spoor]') || !el.dataset.url) return;
    el.dataset.rhAanbevolen = 'bezig';
    try {
      const html = await (await fetch(el.dataset.url)).text();
      const nieuw = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-rh-aanbevolen]');
      if (!nieuw || !nieuw.querySelector('[data-rh-spoor]')) return;
      el.innerHTML = nieuw.innerHTML;
      delete el.dataset.rhKlaar;
      slider(el);
    } catch {}
  };

  const start = (root = document) => {
    inschuiven(root);
    root.querySelectorAll('[data-rh-slider]').forEach(slider);
    root.querySelectorAll('[data-rh-mega]').forEach(mega);
    root.querySelectorAll('[data-rh-aanbevolen]').forEach(aanbevolen);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => start());
  else start();
  document.addEventListener('shopify:section:load', (e) => start(e.target));
})();
